import React, { useRef, useState, useEffect } from "react";
import {
  Clipboard,
  StickyNote,
  User,
  Clock,
  Calendar,
  Video,
  Bold,
  Italic,
  List,
  Eraser,
  Paperclip,
  Download,
} from "lucide-react";
import HistoryPanel from "./HistoryPanel";
import ChecklistGroup from "./editor/ChecklistGroup";
import PurchasedServicesPanel from "./editor/PurchasedServicesPanel";
import UpsellManager from "./editor/UpsellManager";
import ScopeConfiguration from "./editor/ScopeConfiguration";
import HandoverModal from "./editor/HandoverModal";
import EmailGenerator from "./editor/EmailGenerator";
import ClientInfoPanel from "./editor/ClientInfoPanel";
import CaseTimeline from "./editor/CaseTimeline";
import EditorHeader from "./editor/EditorHeader";
import { useCase } from "../context/CaseContext";

export default function EditorContent() {
  const {
    meeting,
    salesNotes,
    attachments,
    notes,
    handleTextChange,
    setNotes,
    tasks,
    loading,
    currentCaseId,
  } = useCase();

  const [showHistory, setShowHistory] = useState(false);
  const notesRef = useRef<HTMLDivElement>(null);

  // Sync notes to editable div only when not focused to avoid cursor jumping
  useEffect(() => {
    if (notesRef.current && notesRef.current.innerHTML !== notes) {
      if (document.activeElement !== notesRef.current) {
        notesRef.current.innerHTML = notes;
      }
    }
  }, [notes]);

  return (
    <div className="min-h-screen font-sans bg-gray-50 dark:bg-slate-900 pb-24 transition-colors duration-200">
      {/* HEADER */}
      <EditorHeader onToggleHistory={() => setShowHistory(true)} />

      {/* MAIN CONTENT */}
      <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6">
        {/* MEETING BANNER SECTION */}
        {meeting && meeting.date && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border-l-4 border-l-teal-500 border-y border-r border-gray-200 dark:border-slate-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in slide-in-from-top-2">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-teal-50 dark:bg-teal-900/20 text-teal-600 dark:text-teal-400 rounded-lg">
                <Calendar size={24} />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 dark:text-white text-base">
                  Kick-off Meeting Scheduled
                </h3>
                <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 mt-1 text-sm text-slate-500 dark:text-slate-400">
                  <span className="flex items-center gap-1.5">
                    <Clock size={14} />
                    {new Date(Number(meeting.date)).toLocaleString([], {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                  <span className="hidden md:inline">•</span>
                  <span className="flex items-center gap-1.5">
                    <User size={14} />
                    {meeting.attendeeName} ({meeting.attendeeEmail})
                  </span>
                </div>
              </div>
            </div>
            {meeting.link && (
              <a
                href={meeting.link}
                target="_blank"
                rel="noreferrer"
                className="whitespace-nowrap px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold text-sm flex items-center gap-2 shadow-sm transition-colors"
              >
                <Video size={16} /> Join Google Meet
              </a>
            )}
          </div>
        )}

        {/* CLIENT INFO PANEL */}
        <ClientInfoPanel />

        {/* SALES NOTES & ATTACHMENTS */}
        {(salesNotes.length > 0 || (attachments && attachments.length > 0)) && (
          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm space-y-6">
            {salesNotes.length > 0 && (
              <div>
                <h4 className="text-xs font-bold uppercase text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
                  <StickyNote size={14} /> HubSpot Sales Handover Notes
                </h4>
                <div
                  className="prose prose-sm dark:prose-invert text-sm text-gray-700 dark:text-gray-300"
                  dangerouslySetInnerHTML={{ __html: salesNotes[0] }}
                />
              </div>
            )}

            {attachments && attachments.length > 0 && (
              <div>
                {salesNotes.length > 0 && (
                  <div className="h-px bg-gray-100 dark:bg-slate-700 my-4" />
                )}
                <h4 className="text-xs font-bold uppercase text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
                  <Paperclip size={14} /> Attachments
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {attachments.map((file, idx) => (
                    <a
                      key={idx}
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors group"
                    >
                      <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 transition-colors">
                        <Paperclip size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {file.size}
                        </p>
                      </div>
                      <div className="text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                        <Download size={16} />
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* PURCHASED SERVICES */}
        <PurchasedServicesPanel />

        {/* UPSELL OPPORTUNITIES */}
        <UpsellManager />

        {/* TECH STACK / SCOPE */}
        <ScopeConfiguration />

        {/* CASE TIMELINE */}
        <CaseTimeline />

        {/* NOTES & CHECKLISTS */}
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
          <div className="p-2 border-b border-gray-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-2">
                Internal Notes
              </h3>
              <div className="h-4 w-px bg-gray-300 dark:bg-slate-600 mx-2" />
              <div className="flex items-center gap-1">
                <button
                  onClick={() => document.execCommand("bold")}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded text-gray-500 dark:text-gray-400"
                  title="Bold"
                >
                  <Bold size={14} />
                </button>
                <button
                  onClick={() => document.execCommand("italic")}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded text-gray-500 dark:text-gray-400"
                  title="Italic"
                >
                  <Italic size={14} />
                </button>
                <button
                  onClick={() => document.execCommand("removeFormat")}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded text-gray-500 dark:text-gray-400"
                  title="Clear Formatting"
                >
                  <Eraser size={14} />
                </button>
                <button
                  onClick={() => document.execCommand("insertUnorderedList")}
                  className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded text-gray-500 dark:text-gray-400"
                  title="Bullet List"
                >
                  <List size={14} />
                </button>
              </div>
            </div>
            {loading && (
              <span className="text-xs text-gray-400 animate-pulse">
                Loading notes...
              </span>
            )}
          </div>
          {loading ? (
            <div className="w-full p-4 min-h-[150px] bg-gray-50 dark:bg-slate-900/50 animate-pulse" />
          ) : (
            <div
              ref={notesRef}
              contentEditable
              className="w-full p-4 min-h-[150px] text-sm bg-transparent outline-none dark:text-white prose prose-sm max-w-none dark:prose-invert focus:ring-0"
              onInput={(e) =>
                handleTextChange(setNotes, "notes", e.currentTarget.innerHTML)
              }
            />
          )}
        </div>

        <div>
          <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2 mt-8">
            <Clipboard className="text-blue-600" /> Onboarding Checklist
          </h2>

          <ChecklistGroup
            title="Admin / Finance"
            cat="admin"
            taskList={tasks.admin}
          />

          <ChecklistGroup
            title="Website Infrastructure"
            cat="infrastructure"
            taskList={tasks.infrastructure}
          />

          <ChecklistGroup
            title="Website Setup"
            cat="website"
            taskList={tasks.website}
          />

          <ChecklistGroup
            title="Platforms"
            cat="platforms"
            taskList={tasks.platforms}
          />

          <ChecklistGroup
            title="Advanced Scope"
            cat="advanced"
            taskList={tasks.advanced}
          />

          <ChecklistGroup
            title="Additional Tasks"
            cat="custom"
            taskList={tasks.custom}
          />
        </div>
      </div>

      {/* HANDOVER MODAL */}
      <HandoverModal />

      {/* EMAIL MODAL */}
      <EmailGenerator />

      <HistoryPanel
        caseId={currentCaseId}
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
      />
    </div>
  );
}
